/**
 * Richieste di iscrizione a un'edizione in attesa di approvazione.
 *
 * Il BE non espone questo stato: all'invio della richiesta l'edizione resta come prima e solo
 * l'approvazione del responsabile la porta a "E". Finché non arriverà uno stato dedicato ("W"),
 * il fatto che la richiesta sia già partita vive lato client, per utente ed edizione, così che
 * ricaricando la pagina il CTA continui a leggere "Richiesta inviata" invece di riproporre
 * l'iscrizione.
 *
 * Una sola chiave per tutti i punti che leggono o scrivono lo stato di approvazione: storage separati
 * darebbero risposte diverse sulla stessa edizione.
 */
const PENDING_APPROVAL_STORAGE_KEY = "enrollment_pending_approval";

export interface PendingApprovalData {
  editionId: number;
  userId: number;
}

export const getPendingApprovalsFromStorage = (): PendingApprovalData[] => {
  const data = sessionStorage.getItem(PENDING_APPROVAL_STORAGE_KEY);
  return data ? JSON.parse(data) : [];
};

export const savePendingApprovalToStorage = (editionId: number, userId: number) => {
  const existing = getPendingApprovalsFromStorage();
  if (!existing.some(item => item.editionId === editionId && item.userId === userId)) {
    existing.push({ editionId, userId });
    sessionStorage.setItem(PENDING_APPROVAL_STORAGE_KEY, JSON.stringify(existing));
  }
};

export const isEditionPendingApproval = (editionId: number, userId: number): boolean => {
  return getPendingApprovalsFromStorage().some(
    item => item.editionId === editionId && item.userId === userId
  );
};

export const clearPendingApprovalFromStorage = (editionId: number, userId: number) => {
  const existing = getPendingApprovalsFromStorage();
  const filtered = existing.filter(item => !(item.editionId === editionId && item.userId === userId));
  sessionStorage.setItem(PENDING_APPROVAL_STORAGE_KEY, JSON.stringify(filtered));
};

/**
 * Una qualunque edizione dell'aula con richiesta in attesa per l'utente corrente.
 * Serve alle card, che ragionano sull'aula e non sulla singola edizione.
 */
export const hasPendingApprovalAmong = (
  editionIds: (number | null | undefined)[],
  userId: number
): boolean => {
  if (!userId) return false;
  const pending = getPendingApprovalsFromStorage();
  return editionIds.some(
    (editionId) => editionId != null && pending.some(
      (item) => item.editionId === editionId && item.userId === userId
    )
  );
};

/**
 * Chiude le richieste registrate su queste edizioni: da chiamare appena il BE conferma
 * l'iscrizione (o il rifiuto), altrimenti la traccia locale sopravvive all'approvazione e la card
 * continua a dichiarare un'attesa che non c'è più.
 * Restituisce true se qualcosa è stato effettivamente rimosso.
 */
export const clearPendingApprovalsFor = (
  editionIds: (number | null | undefined)[],
  userId: number
): boolean => {
  if (!userId) return false;
  const existing = getPendingApprovalsFromStorage();
  if (existing.length === 0) return false;
  const ids = editionIds.filter((editionId): editionId is number => editionId != null);
  const filtered = existing.filter(
    (item) => !(item.userId === userId && ids.includes(item.editionId))
  );
  if (filtered.length === existing.length) return false;
  sessionStorage.setItem(PENDING_APPROVAL_STORAGE_KEY, JSON.stringify(filtered));
  return true;
};
